import { Tab } from "@jugl-web/ui-components";
import { BottomCenteredDrawer } from "@jugl-web/ui-components/web/BottomCenteredDrawer";
import { useTranslations } from "@jugl-web/utils";
import { FC, useMemo, useState } from "react";
import { ControlBar } from "./components/ControlBar";
import { ReactComponent as DialogTitleIcon } from "./assets/dialog-title.svg";
import { CustomerFieldsTab } from "./components/CustomerFieldsTab";

export type CustomerSettingsTabId = "personal" | "company";

interface CustomerSettingsTab {
  id: CustomerSettingsTabId;
  title: string;
  isHidden?: boolean;
}

interface BoardSettingsDialogProps {
  entityId: string;
  isOpen: boolean;
  initialTabId?: CustomerSettingsTabId;
  onClose: () => void;
}

export const CustomizeFieldsDialog: FC<BoardSettingsDialogProps> = ({
  entityId,
  isOpen,
  initialTabId,
  onClose,
}) => {
  const [activeTabId, setActiveTabId] = useState<CustomerSettingsTabId>(
    initialTabId || "personal"
  );

  const { t } = useTranslations();

  const tabs = useMemo<CustomerSettingsTab[]>(
    () => [
      {
        id: "personal" as const,
        title: t({
          id: "customer-page.attributes-settings-personal-info-tab",
          defaultMessage: "Personal Info",
        }),
      },
      {
        id: "company" as const,
        title: t({
          id: "customer-page.attributes-settings-business-info-tab",
          defaultMessage: "Business Info",
        }),
      },
    ],
    [t]
  );

  return (
    <BottomCenteredDrawer
      isOpen={isOpen}
      header={{
        type: "title",
        endSlot: "",
        title: (
          <div className="flex items-center gap-3 text-lg font-semibold text-[#383838]">
            <DialogTitleIcon />
            {t({
              id: "customer-page.attributes-settings-dialog-title",
              defaultMessage: "Manage Customer Attributes",
            })}
          </div>
        ),
      }}
      size="fullscreen"
      onClose={onClose}
    >
      <BottomCenteredDrawer.Content className="flex flex-col">
        <ControlBar>
          <div className="flex items-center gap-1 self-end">
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                title={tab.title}
                isSelected={activeTabId === tab.id}
                onClick={() => setActiveTabId(tab.id)}
              />
            ))}
          </div>
        </ControlBar>
        <div className="flex-1 overflow-x-hidden overflow-y-auto">
          <CustomerFieldsTab entityId={entityId} variant={activeTabId} />
        </div>
      </BottomCenteredDrawer.Content>
    </BottomCenteredDrawer>
  );
};
